/**
 * The role application a page mounts, and the path it is mounted under (COM-452, COM-453).
 *
 * The shell the server sends names both on the element the entry mounts into: the
 * application's surface (`data-compass-surface`) and its router's basename
 * (`data-compass-basename`), which is the application's path prefix, such as `/parents`.
 * The server always gives one since COM-460; it is empty only on a mount root that names
 * none, such as a gallery story mounted without one. Both are read when
 * they are asked for, never when this module loads, so code outside React (the 401
 * handler, a document load) reads the same values as the router.
 */

/** The three role applications, by the path prefix that selects each (`App\Enums\CompassApp`). */
export const COMPASS_APPS = ['parents', 'young-people', 'professionals'] as const;
export type CompassApp = (typeof COMPASS_APPS)[number];

const APP_BY_SURFACE: Record<string, CompassApp> = {
  parent: 'parents',
  'young-person': 'young-people',
  professional: 'professionals',
};

/** The element the shell mounts the application into: `<body>` carries the surface too. */
const mountRoot = () => document.querySelector<HTMLElement>('body [data-compass-surface]');

/** The application this page mounts, whether or not it is served under its prefix. */
export const compassApp = (): CompassApp | null =>
  APP_BY_SURFACE[mountRoot()?.dataset.compassSurface ?? ''] ?? null;

/** The router's basename: the prefix the page is served under, or empty when the mount root names none. */
export const appBasename = (): string => mountRoot()?.dataset.compassBasename ?? '';

/** Local API and session requests must identify the same app as its shell. */
export const appRequestPath = (url: string, basename: string): string => {
  // Exact root paths only: leave third-party URLs, already-prefixed requests,
  // assets and document navigation alone. Preserve the query string verbatim.
  const pathname = url.split(/[?#]/, 1)[0];
  const isAppRequest = pathname === '/api' || pathname.startsWith('/api/') || [
    '/sanctum/csrf-cookie',
    '/login',
    '/logout',
    '/register',
    '/password/email',
    '/password/reset',
    '/password/confirm',
  ].includes(pathname);
  return isAppRequest ? `${basename}${url}` : url;
};

/** A document address as the router sees it: the pathname without its basename. */
export const routerPathname = (pathname: string, basename: string): string => {
  if (basename === '') return pathname;
  if (pathname === basename) return '/';
  return pathname.startsWith(`${basename}/`) ? pathname.slice(basename.length) : pathname;
};

/**
 * The application's landing page, which the Launch site serves outside the application:
 * `/parents` and `/professionals`, and the home page for the Young Person application,
 * which has no landing page of its own.
 */
export const landingPath = (app: CompassApp | null): string =>
  app === 'parents' || app === 'professionals' ? `/${app}` : '/';
